import React, { useEffect, useState } from 'react';
import { PatientLink } from './PatientLink';
import { XMarkIcon, PlusIcon, ClockIcon } from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';
import { SummaryLine } from './SummaryLine';
import { allocateCredits, settlesWithCredits } from './creditAllocation';
import { isDiscountComplete } from './chargeDiscount';
import { WizardModalShell } from './WizardModalShell';
import { WizardStepIndicator, type WizardStepDefinition } from './WizardStepIndicator';
import { WizardSummaryPane } from './WizardSummaryPane';
import {
    useCreateCharge,
    useOneTimeDiscounts,
    usePracticeOptions,
    useSetChargeOneTimeDiscounts,
    useUpdateCharge
} from '../../hooks/useBilling';
import { useToast } from '../../providers/ToastProvider';
import { apiErrorMessage } from '../../utils/apiError';
import {
    applyRules,
    fetchPracticeProductPricing,
    ruleBreakdown,
    rulesForVersion,
    type PricedProduct,
    type PricedRule,
    type PricedVersion
} from '../../services/practiceProductPricing';
import { fetchPatientsPage } from '../../services/patientsV2';
import { isOrthopulseProductName } from '../../services/orthopulseOrder';
import { SearchSelect } from './SearchSelect';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import {
    allocateProportionallyCents,
    formatMoneyCents,
    formatMoneyDollars,
    installmentShareCents
} from '../../utils/money';
import type {
    BillingCharge,
    ChargeDetail,
    LineItemInput,
    OneTimeDiscountListItem
} from '../../services/billing';

interface CreateChargeModalProps {
    onClose: () => void;
    onSuccess: (created: BillingCharge) => void;
    // Preselects the practice (e.g. opened with a practice already selected);
    // the field stays editable.
    initialPracticeId?: string;
    // Edit mode: prefills every field from the charge (practice locked) and
    // PATCHes on submit instead of creating. Only pending charges.
    editCharge?: ChargeDetail;
    onUpdated?: () => void;
}

interface LineItemRow extends LineItemInput {
    _key: number;
    // The base product picked in the dropdown; productVersionId is the resolved (or
    // manually chosen) version of it that actually goes on the charge.
    productId: string;
    // Last description the auto-builder produced for this row. Lets us tell
    // an auto-filled description apart from one the user typed by hand.
    _autoDescription: string;
}

let _keyCounter = 0;
function nextKey() {
    return ++_keyCounter;
}

function emptyLineItem(): LineItemRow {
    return {
        _key: nextKey(),
        productId: '',
        productVersionId: '',
        description: '',
        unitPrice: 0,
        quantity: 1,
        _autoDescription: ''
    };
}

// The product name as it should read on the invoice line: version label
// appended unless it's the single default version.
function productDisplayName(
    priced: PricedProduct | undefined,
    productVersionId: string
): string | undefined {
    if (!priced) return undefined;
    const version = priced.versions.find((v) => v.id === productVersionId);
    if (!version || version.label === 'Standard') return priced.name;
    return `${priced.name} (${version.label})`;
}

// Mirrors the invoice description format built by odo-integrations'
// invoicing.controller.ts, so line items read the same way on both sides.
function buildLineItemDescription(
    productName: string | undefined,
    patientName: string | undefined,
    doctorName: string | null | undefined
): string {
    return [
        productName,
        patientName ? `Patient Name : ${patientName}` : undefined,
        doctorName ? `Doctor Name : ${doctorName}` : undefined
    ]
        .filter((segment): segment is string => Boolean(segment))
        .join(' - ');
}

// Line cents, discounts applied, mirroring charges.process.service.ts's
// invoice amount math so this preview never drifts from what gets invoiced.
function lineCentsFor(item: LineItemInput): number {
    return Math.round(item.unitPrice * 100) * item.quantity;
}

function computeTotalCents(baseCents: number, discountType: string, discountValue: number): number {
    if (!discountType || !discountValue) return baseCents;
    if (discountType === 'flat') return Math.max(0, baseCents - Math.round(discountValue * 100));
    return Math.round(baseCents * (1 - discountValue / 100));
}

function rateLabel(rule: PricedRule): string {
    return rule.discountType === 'percentage'
        ? `-${rule.discountValue}%`
        : `-${formatMoneyDollars(rule.discountValue)}`;
}

function shortRef(id: string): string {
    return id.slice(0, 8).toUpperCase();
}

function fmtExpiry(iso: string): string {
    return new Date(iso).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    });
}

// A manually picked version still gets the practice's stacked discount
// rules - the ones scoped to that version plus the all-version ones.
function effectiveCentsFor(version: PricedVersion, priced: PricedProduct): number {
    return applyRules(version.priceCents, rulesForVersion(priced.rules, version.id));
}

function dueDateFor(daysFromNow: number): string {
    const date = new Date();
    date.setDate(date.getDate() + daysFromNow);
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

const INPUT_CLASS =
    'appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 text-sm text-gray-700 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500';

const FIELD_LABEL_CLASS = 'block text-[13px] font-semibold text-gray-700 mb-1.5';

const SEARCH_DEBOUNCE_MS = 300;

// Three small steps instead of one long form; the live summary pane stays
// visible throughout. Forward jumps require the steps in between to be valid.
const STEPS: readonly WizardStepDefinition[] = [
    { title: 'Details' },
    { title: 'Products' },
    { title: 'Payment' }
];

export function CreateChargeModal({
    onClose,
    onSuccess,
    initialPracticeId,
    editCharge,
    onUpdated
}: CreateChargeModalProps) {
    const { showToast } = useToast();
    const { mutateAsync: createCharge, isPending: isCreating } = useCreateCharge();
    const { mutateAsync: updateChargeMutation, isPending: isUpdating } = useUpdateCharge();
    const { mutateAsync: setChargeCredits, isPending: isSavingCredits } =
        useSetChargeOneTimeDiscounts();
    const isPending = isCreating || isUpdating || isSavingCredits;

    useEscapeKey(onClose, !isPending);
    const isEdit = Boolean(editCharge);
    const { practices, isLoading: practicesLoading } = usePracticeOptions();

    const [practiceId, setPracticeId] = useState(
        editCharge?.practice.id ?? initialPracticeId ?? ''
    );
    const [patientId, setPatientId] = useState(editCharge?.patientId ?? '');
    const [description, setDescription] = useState(editCharge?.description ?? '');

    const selectedPractice = practices.find((p) => p.id === practiceId);

    // Practice-specific product pricing, fetched once a practice is selected -
    // the picker stays disabled until then since prices resolve per practice.
    const { data: pricingData, isLoading: pricingLoading } = useQuery({
        queryKey: ['v2/practices', practiceId, 'product-pricing'],
        queryFn: () => fetchPracticeProductPricing(practiceId),
        enabled: Boolean(practiceId),
        staleTime: 5 * 60 * 1000
    });

    // Unapplied one-time discounts offered as attachable credits.
    const { oneTimeDiscounts } = useOneTimeDiscounts(
        {
            page: 1,
            // The endpoint's max: this list has no pagination, so anything
            // beyond the first page would be unreachable.
            limit: 100,
            practiceId: practiceId || undefined,
            status: ['available', 'expiring'],
            patientId: patientId || undefined,
            includeUnassigned: true,
            sort: 'expiry',
            order: 'asc'
        },
        Boolean(practiceId)
    );
    const linkedCreditIds = React.useMemo(
        () => new Set((editCharge?.oneTimeDiscounts ?? []).map((d) => d.id)),
        [editCharge]
    );
    const availableCredits = React.useMemo(() => {
        const fromList = oneTimeDiscounts.filter(
            (d) => d.status === 'available' || d.status === 'expiring' || linkedCreditIds.has(d.id)
        );
        // Linked credits outside the fetched page must still render.
        const missing = (editCharge?.oneTimeDiscounts ?? [])
            .filter((d) => !fromList.some((f) => f.id === d.id))
            .map((d): OneTimeDiscountListItem => ({
                id: d.id,
                chargeId: d.chargeId,
                chargeStatus: null,
                sourceChargeId: d.sourceChargeId,
                sourceChargeDescription: d.sourceCharge?.description ?? null,
                amount: d.amount,
                description: d.description,
                expiresAt: d.expiresAt,
                createdAt: d.createdAt,
                practiceId: null,
                practiceName: null,
                practiceDoctorName: null,
                patientId: null,
                patientName: null,
                status: 'applied'
            }));
        const all = [...missing, ...fromList];
        // Linked ones first so the pre-checked rows sit on top.
        return [
            ...all.filter((d) => linkedCreditIds.has(d.id)),
            ...all.filter((d) => !linkedCreditIds.has(d.id))
        ];
    }, [oneTimeDiscounts, editCharge, linkedCreditIds]);
    const [selectedCreditIds, setSelectedCreditIds] = useState<string[]>(() =>
        (editCharge?.oneTimeDiscounts ?? []).map((d) => d.id)
    );
    const toggleCredit = (id: string) => {
        setSelectedCreditIds((prev) =>
            prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]
        );
    };

    // Patients for the selected practice, offered as an optional link. The
    // list endpoint filters by practice name (not id) and supports server search.
    const [patientSearch, setPatientSearch] = useState('');
    const [debouncedPatientSearch, setDebouncedPatientSearch] = useState('');
    useEffect(() => {
        const handle = setTimeout(
            () => setDebouncedPatientSearch(patientSearch.trim()),
            SEARCH_DEBOUNCE_MS
        );
        return () => clearTimeout(handle);
    }, [patientSearch]);

    const practiceName = selectedPractice?.name;
    const { data: patientsData, isLoading: patientsLoading } = useQuery({
        queryKey: ['v2/patients', practiceName, debouncedPatientSearch],
        queryFn: () => {
            if (!practiceName)
                return Promise.resolve({
                    data: [],
                    meta: { total: 0, page: 1, limit: 100, totalPages: 0 }
                });
            return fetchPatientsPage({
                page: 1,
                limit: 100,
                sort: 'patient',
                order: 'asc',
                practice: [practiceName],
                search: debouncedPatientSearch || undefined
            });
        },
        enabled: Boolean(practiceName),
        staleTime: 60 * 1000
    });
    // The picked patient, pinned by id+label: the options list only holds the
    // fetched page, so a search-found patient would look unselected after reset.
    const [pickedPatient, setPickedPatient] = useState<{ value: string; label: string } | null>(
        editCharge?.patientId
            ? {
                  value: editCharge.patientId,
                  label: editCharge.patientName ?? `#${editCharge.patientId}`
              }
            : null
    );
    const patientOptions = React.useMemo(() => {
        const options = (patientsData?.data ?? []).map((p) => ({ value: p.id, label: p.name }));
        // The selected patient must stay selectable/displayable even when
        // outside the currently fetched page.
        if (pickedPatient && !options.some((o) => o.value === pickedPatient.value)) {
            options.unshift(pickedPatient);
        }
        return options;
    }, [patientsData, pickedPatient]);
    // Feed the line-item description builder: the linked patient's name (if
    // any) and the selected practice's doctor name (if any).
    const selectedPatientName = patientId
        ? patientOptions.find((p) => p.value === patientId)?.label
        : undefined;
    const doctorName = selectedPractice?.doctorName ?? undefined;

    const handlePatientChange = (id: string) => {
        setPatientId(id);
        const option = patientOptions.find((o) => o.value === id);
        setPickedPatient(option ?? null);
        // Credits belong to a patient, so a pick made for the previous one no
        // longer applies. Already-linked credits stay checked to avoid unlinking.
        setSelectedCreditIds((prev) => prev.filter((creditId) => linkedCreditIds.has(creditId)));
    };

    const handlePracticeChange = (id: string) => {
        setPracticeId(id);
        setPatientId('');
        setPickedPatient(null);
        setPatientSearch('');
        setDebouncedPatientSearch('');
        setSelectedCreditIds([]);
        // Versions and prices resolve per practice; picked products stay but
        // their version/price must re-derive against the new practice's pricing.
        setLineItems([emptyLineItem()]);
    };

    // Map base product id → practice pricing row.
    const pricingMap = React.useMemo<Record<string, PricedProduct>>(() => {
        if (!pricingData) return {};
        return Object.fromEntries(pricingData.map((p) => [p.id, p]));
    }, [pricingData]);

    const [lineItems, setLineItems] = useState<LineItemRow[]>(() =>
        editCharge && editCharge.lineItems.length > 0
            ? editCharge.lineItems.map((li) => ({
                  _key: nextKey(),
                  id: li.id,
                  productId: li.productId,
                  productVersionId: li.productVersionId,
                  description: li.description,
                  unitPrice: Number(li.unitPrice),
                  quantity: li.quantity,
                  _autoDescription: ''
              }))
            : [emptyLineItem()]
    );
    const [discountType, setDiscountType] = useState<'flat' | 'percentage' | ''>(
        (editCharge?.discountType as 'flat' | 'percentage' | null) ?? ''
    );
    const [discountValue, setDiscountValue] = useState<string>(
        editCharge?.discountValue ? String(Number(editCharge.discountValue)) : ''
    );
    const [installments, setInstallments] = useState<number>(editCharge?.installments ?? 1);
    const [paymentTerms, setPaymentTerms] = useState<number[]>(editCharge?.paymentTerms ?? [0]);
    const [sendPurchaseOrder, setSendPurchaseOrder] = useState(false);

    // Re-derive each line item's description on patient/doctor change, without
    // clobbering a description the user edited by hand.
    useEffect(() => {
        setLineItems((prev) =>
            prev.map((li) => {
                if (!li.productId) return li;
                const displayName = productDisplayName(
                    pricingMap[li.productId],
                    li.productVersionId
                );
                const autoDescription = buildLineItemDescription(
                    displayName,
                    selectedPatientName,
                    doctorName
                );
                if (autoDescription === li._autoDescription) return li;
                const shouldAutoFill =
                    li.description === '' || li.description === li._autoDescription;
                return {
                    ...li,
                    description: shouldAutoFill ? autoDescription : li.description,
                    _autoDescription: autoDescription
                };
            })
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPatientName, doctorName]);

    // Same option shape as the charges-list Practice filter: name + doctor sublabel.
    const practiceOptions = React.useMemo(
        () =>
            practices.map((p) =>
                p.doctorName
                    ? {
                          value: p.id,
                          label: p.name,
                          sublabel: `Dr. ${p.doctorName}`,
                          keywords: [`Dr. ${p.doctorName}`, p.doctorName]
                      }
                    : { value: p.id, label: p.name }
            ),
        [practices]
    );

    // One option per base product; unresolved ones flag that a version must
    // be picked below.
    const productOptions = React.useMemo(
        () =>
            (pricingData ?? []).map((p) => {
                const discounted =
                    p.effectivePriceCents !== null && p.effectivePriceCents !== p.defaultPriceCents;
                const sublabel =
                    p.effectivePriceCents !== null
                        ? discounted
                            ? `${formatMoneyCents(p.defaultPriceCents ?? 0)} · ${p.rules.length} discount${p.rules.length === 1 ? '' : 's'} → ${formatMoneyCents(p.effectivePriceCents)}`
                            : formatMoneyCents(p.effectivePriceCents)
                        : `${p.versions.length} versions - pick one`;
                return {
                    value: p.id,
                    label: p.name,
                    sublabel,
                    keywords: [p.category, p.subCategory, ...p.versions.map((v) => v.label)].filter(
                        Boolean
                    ) as string[]
                };
            }),
        [pricingData]
    );

    const hasOrthopulseLineItem = lineItems.some(
        (li) => li.productId && isOrthopulseProductName(pricingMap[li.productId]?.name)
    );

    const lineCentsList = lineItems.map(lineCentsFor);
    const baseCents = lineCentsList.reduce((sum, cents) => sum + cents, 0);
    const totalCents = computeTotalCents(
        baseCents,
        discountType,
        discountValue ? Number(discountValue) : 0
    );
    const selectedCredits = availableCredits.filter((d) => selectedCreditIds.includes(d.id));
    const creditAllocation = allocateCredits(selectedCredits, totalCents);
    // What the practice actually pays: total net of attached credits, split
    // via the same largest-remainder allocation charges.process.service.ts uses.
    const netCents = totalCents - creditAllocation.usedCents;
    const payableLineCents = allocateProportionallyCents(lineCentsList, netCents);
    const installmentCentsAmounts = Array.from({ length: installments }, (_, i) =>
        payableLineCents.reduce(
            (sum, cents) => sum + installmentShareCents(cents, installments, i),
            0
        )
    );

    const handleInstallmentsChange = (n: number) => {
        const count = Math.min(12, Math.max(1, n));
        setInstallments(count);
        setPaymentTerms((prev) => {
            const next = [...prev];
            // Default new installments to 30-day steps (Net 0 / 30 / 60 …).
            while (next.length < count) next.push(next.length * 30);
            return next.slice(0, count);
        });
    };

    const applyProductPick = (li: LineItemRow, productId: string): LineItemRow => {
        const priced = pricingMap[productId];
        // Auto-resolved version when the practice's attributes pick one; blank
        // otherwise, forcing the explicit version dropdown below.
        const productVersionId = priced?.resolvedVersionId ?? '';
        const displayName = productDisplayName(priced, productVersionId);
        const autoDescription = buildLineItemDescription(
            displayName,
            selectedPatientName,
            doctorName
        );
        const shouldAutoFill = li.description === '' || li.description === li._autoDescription;
        return {
            ...li,
            productId,
            productVersionId,
            description: shouldAutoFill ? autoDescription : li.description,
            _autoDescription: autoDescription,
            unitPrice:
                priced?.effectivePriceCents !== null && priced?.effectivePriceCents !== undefined
                    ? priced.effectivePriceCents / 100
                    : 0
        };
    };

    const applyVersionPick = (li: LineItemRow, productVersionId: string): LineItemRow => {
        const priced = pricingMap[li.productId];
        const version = priced?.versions.find((v) => v.id === productVersionId);
        const displayName = productDisplayName(priced, productVersionId);
        const autoDescription = buildLineItemDescription(
            displayName,
            selectedPatientName,
            doctorName
        );
        const shouldAutoFill = li.description === '' || li.description === li._autoDescription;
        return {
            ...li,
            productVersionId,
            description: shouldAutoFill ? autoDescription : li.description,
            _autoDescription: autoDescription,
            unitPrice: priced && version ? effectiveCentsFor(version, priced) / 100 : li.unitPrice
        };
    };

    const handleLineItemChange = (
        key: number,
        field: 'productId' | 'productVersionId' | 'description' | 'unitPrice' | 'quantity',
        raw: string
    ) => {
        setLineItems((prev) =>
            prev.map((li) => {
                if (li._key !== key) return li;
                if (field === 'productId') return applyProductPick(li, raw);
                if (field === 'productVersionId') return applyVersionPick(li, raw);
                if (field === 'unitPrice') return { ...li, unitPrice: Number(raw) || 0 };
                if (field === 'quantity') return { ...li, quantity: Math.max(1, Number(raw) || 1) };
                return { ...li, [field]: raw };
            })
        );
    };

    const stepQuantity = (key: number, delta: number) => {
        setLineItems((prev) =>
            prev.map((li) =>
                li._key === key ? { ...li, quantity: Math.max(1, li.quantity + delta) } : li
            )
        );
    };

    const addLineItem = () => setLineItems((prev) => [...prev, emptyLineItem()]);

    const removeLineItem = (key: number) => {
        setLineItems((prev) => (prev.length > 1 ? prev.filter((li) => li._key !== key) : prev));
    };

    const [step, setStep] = useState(0);
    const lineItemsValid = lineItems.every(
        (li) =>
            li.productId && li.productVersionId && li.unitPrice > 0 && li.description.trim() !== ''
    );
    const maxReachable = !practiceId ? 0 : !lineItemsValid ? 1 : 2;

    const goNext = () => {
        if (step === 0 && !practiceId) {
            showToast('Please select a practice', 'error');
            return;
        }
        if (step === 1 && !lineItemsValid) {
            showToast(
                'Each line item needs a product, version, positive price, and description',
                'error'
            );
            return;
        }
        setStep((s) => Math.min(STEPS.length - 1, s + 1));
    };

    // Resolves to the server's message when the credits could not be saved.
    const saveCredits = async (chargeId: string): Promise<string | null> => {
        try {
            await setChargeCredits({ chargeId, oneTimeDiscountIds: selectedCreditIds });
            return null;
        } catch (error) {
            return apiErrorMessage(error, 'the request failed');
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        // Implicit form submission (Enter in any input) fires on every step -
        // before the last step it must ADVANCE, not create the charge.
        if (step < STEPS.length - 1) {
            goNext();
            return;
        }
        if (!practiceId) {
            showToast('Please select a practice', 'error');
            return;
        }
        if (lineItems.some((li) => !li.productId || li.unitPrice <= 0)) {
            showToast('Each line item needs a product and a positive price', 'error');
            return;
        }
        if (lineItems.some((li) => !li.productVersionId)) {
            showToast('Pick a version for every product', 'error');
            return;
        }
        if (!isDiscountComplete(discountType, discountValue)) {
            showToast('Enter a discount above 0, or set the commercial discount to None', 'error');
            return;
        }

        try {
            if (isEdit && editCharge) {
                await updateChargeMutation({
                    id: editCharge.id,
                    changes: {
                        patientId: patientId || null,
                        description: description.trim() || null,
                        lineItems: lineItems.map((li) => ({
                            id: li.id,
                            productVersionId: li.productVersionId,
                            description: li.description,
                            unitPrice: li.unitPrice,
                            quantity: li.quantity
                        })),
                        discountType: discountType || null,
                        discountValue: discountType && discountValue ? Number(discountValue) : null,
                        installments,
                        paymentTerms
                    }
                });
                // The charge fields are saved from here on, so a credit failure
                // is reported on its own; the credits roll back as a whole.
                const creditsChanged =
                    selectedCreditIds.length !== linkedCreditIds.size ||
                    selectedCreditIds.some((id) => !linkedCreditIds.has(id));
                const creditError = creditsChanged ? await saveCredits(editCharge.id) : null;
                if (creditError) {
                    showToast(
                        `Charge saved, but its credits were not changed: ${creditError}`,
                        'error'
                    );
                    onUpdated?.();
                    return;
                }
                showToast('Charge updated', 'success');
                onUpdated?.();
                return;
            }

            const created = await createCharge({
                practiceId,
                patientId: patientId || undefined,
                description: description.trim() || undefined,
                lineItems: lineItems.map((li) => ({
                    productVersionId: li.productVersionId,
                    description: li.description,
                    unitPrice: li.unitPrice,
                    quantity: li.quantity
                })),
                discountType: discountType || undefined,
                discountValue: discountType && discountValue ? Number(discountValue) : undefined,
                installments,
                paymentTerms,
                sendPurchaseOrder:
                    hasOrthopulseLineItem && sendPurchaseOrder && patientId ? true : undefined
            });
            // The charge exists from here on: a credit failure must not undo it.
            const creditError = selectedCreditIds.length > 0 ? await saveCredits(created.id) : null;
            if (creditError) {
                showToast(
                    `Charge created without credits (${creditError}). Edit the charge to attach them.`,
                    'error'
                );
                onSuccess(created);
                return;
            }
            if (created.purchaseOrder?.sent) {
                showToast(
                    `Charge created. Purchase order ${created.purchaseOrder.poNumber} sent.`,
                    'success'
                );
            } else if (created.purchaseOrder && !created.purchaseOrder.sent) {
                showToast(
                    `Charge created. Purchase order not sent: ${created.purchaseOrder.reason}`,
                    'error'
                );
            } else {
                showToast('Charge created. Process it to generate invoices', 'success');
            }
            onSuccess(created);
        } catch (error) {
            showToast(apiErrorMessage(error, 'Failed to create charge'), 'error');
        }
    };

    return (
        <WizardModalShell
            title={
                isEdit ? `Edit charge #${editCharge!.id.slice(0, 8).toUpperCase()}` : 'New charge'
            }
            onClose={onClose}
            dismissible={!isPending}
            labelledBy="create-charge-title"
            stepIndicator={
                <WizardStepIndicator
                    steps={STEPS}
                    step={step}
                    maxReachable={maxReachable}
                    onStepClick={setStep}
                />
            }
            onSubmit={(e) => {
                void handleSubmit(e);
            }}
            primaryActions={
                step < STEPS.length - 1 ? (
                    <button
                        key="continue"
                        type="button"
                        onClick={(e) => {
                            e.preventDefault();
                            goNext();
                        }}
                        className="bg-black text-white rounded-xl px-6 py-2 text-sm font-semibold hover:bg-gray-900 transition-colors shadow"
                    >
                        Continue
                    </button>
                ) : (
                    <button
                        key="submit"
                        type="submit"
                        disabled={isPending}
                        className="bg-black text-white rounded-xl px-6 py-2 text-sm font-semibold hover:bg-gray-900 transition-colors shadow disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isPending
                            ? isEdit
                                ? 'Saving…'
                                : 'Creating…'
                            : isEdit
                              ? 'Save changes'
                              : 'Create charge'}
                    </button>
                )
            }
            summaryPane={
                <WizardSummaryPane>
                    {selectedPractice && (
                        <div className="flex flex-col gap-2">
                            <SummaryLine label="Practice" value={selectedPractice.name} />
                            {selectedPractice.doctorName && (
                                <SummaryLine
                                    label="Doctor"
                                    value={`Dr. ${selectedPractice.doctorName}`}
                                />
                            )}
                            {patientId && (
                                <SummaryLine
                                    label="Patient"
                                    value={
                                        <PatientLink
                                            patientId={patientId}
                                            patientName={selectedPatientName}
                                        >
                                            {patientOptions.find((p) => p.value === patientId)
                                                ?.label ?? '-'}
                                        </PatientLink>
                                    }
                                />
                            )}
                        </div>
                    )}
                    <div className="flex flex-col gap-2">
                        {lineItems
                            .filter((li) => li.productId)
                            .map((li) => {
                                const name =
                                    productDisplayName(
                                        pricingMap[li.productId],
                                        li.productVersionId
                                    ) ?? 'Product';
                                return (
                                    <SummaryLine
                                        key={li._key}
                                        label={`${name}${li.quantity > 1 ? ` ×${li.quantity}` : ''}`}
                                        value={formatMoneyCents(lineCentsFor(li))}
                                    />
                                );
                            })}
                        {discountType && discountValue && (
                            <SummaryLine
                                label={`Discount (${discountType === 'flat' ? formatMoneyDollars(Number(discountValue)) : `${discountValue}%`})`}
                                value={`−${formatMoneyCents(baseCents - totalCents)}`}
                                minus
                            />
                        )}
                        <SummaryLine
                            label="Charge total"
                            value={formatMoneyCents(totalCents)}
                            strong
                        />
                        {creditAllocation.uses.map(({ credit, usedCents, returnedCents }) => (
                            <SummaryLine
                                key={credit.id}
                                label={
                                    credit.description ||
                                    `One-time discount #${shortRef(credit.id)}`
                                }
                                value={
                                    usedCents === 0 ? (
                                        <span className="text-xs text-gray-500">not needed</span>
                                    ) : (
                                        <>
                                            {returnedCents > 0 && (
                                                <span className="mr-1.5 text-xs text-gray-500">
                                                    of {formatMoneyCents(usedCents + returnedCents)}
                                                </span>
                                            )}
                                            −{formatMoneyCents(usedCents)}
                                        </>
                                    )
                                }
                                minus
                            />
                        ))}
                        {selectedCredits.length > 0 && (
                            <SummaryLine
                                label="Net to pay"
                                value={formatMoneyCents(netCents)}
                                strong
                            />
                        )}
                        {creditAllocation.returnedCents > 0 && (
                            <div className="flex flex-col gap-0.5">
                                <SummaryLine
                                    label="Returned as credit"
                                    value={formatMoneyCents(creditAllocation.returnedCents)}
                                />
                                <span className="text-xs text-gray-500">
                                    Kept for this practice when the charge is processed
                                </span>
                            </div>
                        )}
                    </div>

                    {installments > 1 && (
                        <div className="border-t border-gray-200 pt-4 flex flex-col gap-1.5">
                            <span className="text-xs font-semibold text-gray-500">
                                {installments} installments
                            </span>
                            {installmentCentsAmounts.map((amount, i) => (
                                <div key={i} className="flex justify-between text-xs text-gray-500">
                                    <span>
                                        {dueDateFor(paymentTerms[i] ?? 0)} · Net{' '}
                                        {paymentTerms[i] ?? 0}
                                    </span>
                                    <span className="tabular-nums text-gray-700">
                                        {formatMoneyCents(amount)}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="flex items-start gap-2 text-[11px] text-gray-400 mt-auto pt-4">
                        <ClockIcon className="h-3.5 w-3.5 shrink-0 mt-px" />
                        <span>
                            {settlesWithCredits(creditAllocation.usedCents, netCents)
                                ? 'Settled as paid when the charge is processed; nothing is sent to Bill.com.'
                                : 'Invoices are generated when the charge is processed, and Bill.com emails them to the practice.'}
                        </span>
                    </div>
                </WizardSummaryPane>
            }
        >
            {step === 0 && (
                <>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className={FIELD_LABEL_CLASS}>Practice *</label>
                            <SearchSelect
                                value={practiceId}
                                options={practiceOptions}
                                onChange={handlePracticeChange}
                                placeholder="Select practice…"
                                searchPlaceholder="Search practices..."
                                disabled={practicesLoading || isEdit}
                            />
                        </div>
                        <div>
                            <label className={FIELD_LABEL_CLASS}>Patient (optional)</label>
                            <SearchSelect
                                value={patientId}
                                options={patientOptions}
                                onChange={handlePatientChange}
                                onSearchChange={setPatientSearch}
                                placeholder={
                                    practiceId ? 'Select patient…' : 'Select a practice first'
                                }
                                searchPlaceholder="Search patients..."
                                disabled={!practiceId || patientsLoading}
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 gap-4">
                        <div>
                            <label className={FIELD_LABEL_CLASS}>Description (optional)</label>
                            <input
                                type="text"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="e.g. Full treatment fee"
                                className={INPUT_CLASS}
                            />
                        </div>
                    </div>

                    {selectedPractice?.doctorName && (
                        <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 flex flex-wrap gap-x-8 gap-y-1.5">
                            <span className="text-xs text-gray-500">
                                Doctor{' '}
                                <span className="text-gray-800 font-medium">
                                    Dr. {selectedPractice.doctorName}
                                </span>
                            </span>
                        </div>
                    )}
                </>
            )}

            {step === 1 && (
                <>
                    {/* Line items */}
                    <div className="flex flex-col gap-2.5">
                        <span className="text-[13px] font-semibold text-gray-700">
                            Line items *
                        </span>
                        {lineItems.map((li) => {
                            const priced = li.productId ? pricingMap[li.productId] : undefined;
                            const pickedVersion = priced?.versions.find(
                                (v) => v.id === li.productVersionId
                            );
                            // Unresolved for the practice, the pick is explicit even with
                            // a single chargeable version (a deliberate override).
                            const needsVersionPick = Boolean(priced && !priced.resolvedVersionId);
                            return (
                                <div
                                    key={li._key}
                                    className="border border-gray-200 rounded-lg px-3.5 py-3 flex flex-col gap-2.5"
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="flex-1 min-w-0">
                                            <SearchSelect
                                                value={li.productId}
                                                options={productOptions}
                                                onChange={(v) =>
                                                    handleLineItemChange(li._key, 'productId', v)
                                                }
                                                placeholder={
                                                    practiceId
                                                        ? 'Product…'
                                                        : 'Select a practice first'
                                                }
                                                searchPlaceholder="Search products..."
                                                disabled={!practiceId || pricingLoading}
                                            />
                                        </div>
                                        <div className="flex items-center border border-gray-200 rounded-lg h-9 overflow-hidden shrink-0">
                                            <button
                                                type="button"
                                                onClick={() => stepQuantity(li._key, -1)}
                                                className="w-7 h-9 flex items-center justify-center text-gray-400 hover:bg-gray-50"
                                                aria-label="Decrease quantity"
                                            >
                                                −
                                            </button>
                                            <span className="w-8 text-center text-sm text-gray-800 tabular-nums">
                                                {li.quantity}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => stepQuantity(li._key, 1)}
                                                className="w-7 h-9 flex items-center justify-center text-gray-400 hover:bg-gray-50"
                                                aria-label="Increase quantity"
                                            >
                                                +
                                            </button>
                                        </div>
                                        <div className="relative w-28 shrink-0">
                                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">
                                                $
                                            </span>
                                            <input
                                                type="number"
                                                min="0.01"
                                                step="0.01"
                                                value={li.unitPrice || ''}
                                                onChange={(e) =>
                                                    handleLineItemChange(
                                                        li._key,
                                                        'unitPrice',
                                                        e.target.value
                                                    )
                                                }
                                                required
                                                placeholder="0.00"
                                                className={`${INPUT_CLASS} pl-7`}
                                            />
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => removeLineItem(li._key)}
                                            disabled={lineItems.length === 1}
                                            className="text-gray-300 hover:text-red-500 transition-colors disabled:opacity-30 shrink-0"
                                            aria-label="Remove line item"
                                        >
                                            <XMarkIcon className="h-4 w-4" />
                                        </button>
                                    </div>

                                    {/* Version: auto-resolved → informational chip; unresolved
                        (multiple candidates for this practice) → explicit pick. */}
                                    {needsVersionPick && (
                                        <div className="flex items-center gap-2">
                                            <span className="text-[11px] font-semibold text-amber-600 shrink-0">
                                                Version *
                                            </span>
                                            <select
                                                value={li.productVersionId}
                                                onChange={(e) =>
                                                    handleLineItemChange(
                                                        li._key,
                                                        'productVersionId',
                                                        e.target.value
                                                    )
                                                }
                                                className={`${INPUT_CLASS} !w-auto !py-1.5 text-xs`}
                                            >
                                                <option value="" disabled>
                                                    Pick a version…
                                                </option>
                                                {priced!.versions.map((version) => (
                                                    <option key={version.id} value={version.id}>
                                                        {version.label} ·{' '}
                                                        {formatMoneyCents(
                                                            effectiveCentsFor(version, priced!)
                                                        )}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    )}
                                    {!needsVersionPick &&
                                        pickedVersion &&
                                        priced &&
                                        priced.versions.length > 1 && (
                                            <div className="flex items-center gap-2">
                                                <span className="inline-flex text-[11px] font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                                                    {pickedVersion.label} version
                                                </span>
                                                <span className="text-[11px] text-gray-400">
                                                    auto-selected for this practice
                                                </span>
                                            </div>
                                        )}
                                    {/* Price narration: the walk from list price through each
                        applicable rule to the practice price on this row. */}
                                    {priced &&
                                        pickedVersion &&
                                        rulesForVersion(priced.rules, pickedVersion.id).length >
                                            0 && (
                                            <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 flex flex-col gap-1">
                                                <div className="flex items-center justify-between gap-3">
                                                    <span className="text-[11px] text-gray-500">
                                                        List price
                                                    </span>
                                                    <span className="text-[11px] text-gray-600 tabular-nums">
                                                        {formatMoneyCents(pickedVersion.priceCents)}
                                                    </span>
                                                </div>
                                                {ruleBreakdown(
                                                    pickedVersion.priceCents,
                                                    rulesForVersion(priced.rules, pickedVersion.id)
                                                ).map(({ rule, deltaCents }) => (
                                                    <div
                                                        key={rule.id}
                                                        className="flex items-center justify-between gap-3"
                                                    >
                                                        <span className="text-[11px] text-green-700 truncate">
                                                            {rule.name} ({rateLabel(rule)})
                                                        </span>
                                                        <span className="text-[11px] text-green-700 tabular-nums shrink-0">
                                                            −{formatMoneyCents(deltaCents)}
                                                        </span>
                                                    </div>
                                                ))}
                                                <div className="flex items-center justify-between gap-3 border-t border-gray-200 pt-1">
                                                    <span className="text-[11px] font-semibold text-gray-700">
                                                        Practice price
                                                    </span>
                                                    <span className="text-[11px] font-semibold text-gray-900 tabular-nums">
                                                        {formatMoneyCents(
                                                            effectiveCentsFor(pickedVersion, priced)
                                                        )}
                                                    </span>
                                                </div>
                                                {Math.round(li.unitPrice * 100) !==
                                                    effectiveCentsFor(pickedVersion, priced) && (
                                                    <span className="text-[10px] text-amber-600">
                                                        Unit price manually set to{' '}
                                                        {formatMoneyDollars(li.unitPrice)}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    <input
                                        type="text"
                                        value={li.description}
                                        onChange={(e) =>
                                            handleLineItemChange(
                                                li._key,
                                                'description',
                                                e.target.value
                                            )
                                        }
                                        required
                                        placeholder="Line description (shown on the invoice)"
                                        className={INPUT_CLASS}
                                    />
                                </div>
                            );
                        })}
                        <button
                            type="button"
                            onClick={addLineItem}
                            className="h-10 rounded-lg border border-dashed border-gray-300 text-[13px] font-semibold text-gray-500 hover:bg-gray-50 flex items-center justify-center gap-2 transition-colors"
                        >
                            <PlusIcon className="h-4 w-4" />
                            Add product
                        </button>
                    </div>
                </>
            )}

            {step === 2 && (
                <>
                    {/* Installments + payment terms */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className={FIELD_LABEL_CLASS}>Installments</label>
                            <input
                                type="number"
                                min="1"
                                max="12"
                                step="1"
                                value={installments}
                                onChange={(e) => handleInstallmentsChange(Number(e.target.value))}
                                required
                                className={INPUT_CLASS}
                            />
                        </div>
                        <div>
                            <label className={FIELD_LABEL_CLASS}>Payment terms (days)</label>
                            <div className="flex flex-wrap gap-2">
                                {paymentTerms.map((days, i) => (
                                    <input
                                        key={i}
                                        type="number"
                                        min="0"
                                        step="1"
                                        value={days}
                                        onChange={(e) => {
                                            const next = [...paymentTerms];
                                            next[i] = Math.max(0, Number(e.target.value) || 0);
                                            setPaymentTerms(next);
                                        }}
                                        className={`${INPUT_CLASS} flex-1 min-w-[72px]`}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* Commercial discount */}
            {step === 2 && (
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className={FIELD_LABEL_CLASS}>Commercial discount</label>
                        <select
                            value={discountType}
                            onChange={(e) =>
                                setDiscountType(e.target.value as 'flat' | 'percentage' | '')
                            }
                            className={INPUT_CLASS}
                        >
                            <option value="">None</option>
                            <option value="flat">Flat ($)</option>
                            <option value="percentage">Percentage (%)</option>
                        </select>
                    </div>
                    {discountType && (
                        <div>
                            <label className={FIELD_LABEL_CLASS}>
                                {discountType === 'flat' ? 'Amount ($)' : 'Percentage (%)'}
                            </label>
                            <input
                                type="number"
                                min="0"
                                step={discountType === 'flat' ? '0.01' : '0.1'}
                                value={discountValue}
                                onChange={(e) => setDiscountValue(e.target.value)}
                                placeholder={discountType === 'flat' ? '0.00' : '0'}
                                className={INPUT_CLASS}
                            />
                        </div>
                    )}
                </div>
            )}

            {step === 2 && !isEdit && hasOrthopulseLineItem && patientId && (
                <label className="flex items-center gap-2.5 border border-gray-200 rounded-lg px-3.5 py-2.5 cursor-pointer hover:bg-gray-50 transition-colors">
                    <input
                        type="checkbox"
                        checked={sendPurchaseOrder}
                        onChange={(e) => setSendPurchaseOrder(e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-gray-900 focus:ring-gray-500 shrink-0"
                    />
                    <span className="text-sm text-gray-700">
                        Also send the purchase order to the vendor
                    </span>
                </label>
            )}

            {/* Attachable one-time discounts, checked = linked (save diffs into
                  link/unlink calls). Always visible once a practice is picked. */}
            {step === 2 && practiceId && (
                <div className="flex flex-col gap-2.5">
                    <span className="text-[13px] font-semibold text-gray-700">
                        One-time discounts
                    </span>
                    {availableCredits.length === 0 && (
                        <p className="text-sm text-gray-400 border border-dashed border-gray-200 rounded-lg px-3.5 py-3">
                            {patientId
                                ? 'No unapplied one-time discounts for this patient.'
                                : 'No unapplied one-time discounts without a patient. Pick a patient to see the credits they earned.'}
                        </p>
                    )}
                    <div className="flex flex-col gap-2 max-h-56 overflow-y-auto pr-0.5">
                        {availableCredits.map((credit: OneTimeDiscountListItem) => {
                            const isSelected = selectedCreditIds.includes(credit.id);
                            return (
                                <label
                                    key={credit.id}
                                    className={`flex items-center gap-3 border rounded-lg px-3.5 py-2.5 cursor-pointer transition-colors shrink-0 ${
                                        isSelected
                                            ? 'border-gray-800 bg-gray-50 ring-2 ring-gray-100'
                                            : 'border-gray-200 hover:bg-gray-50'
                                    }`}
                                >
                                    <input
                                        type="checkbox"
                                        checked={isSelected}
                                        onChange={() => toggleCredit(credit.id)}
                                        className="h-4 w-4 rounded border-gray-300 text-gray-900 focus:ring-gray-500 shrink-0"
                                    />
                                    <div className="min-w-0 flex-1">
                                        <span className="text-sm text-gray-800 truncate block">
                                            {credit.description ||
                                                `One-time discount #${shortRef(credit.id)}`}
                                            {credit.patientName && (
                                                <span className="text-gray-400">
                                                    {' '}
                                                    · {credit.patientName}
                                                </span>
                                            )}
                                        </span>
                                        <span
                                            className={`text-[11px] ${credit.status === 'expiring' ? 'text-amber-600 font-medium' : 'text-gray-400'}`}
                                        >
                                            {credit.expiresAt
                                                ? `expires ${fmtExpiry(credit.expiresAt)}`
                                                : 'no expiry'}
                                        </span>
                                    </div>
                                    <span className="text-sm font-semibold text-green-700 tabular-nums shrink-0">
                                        −{formatMoneyDollars(Number(credit.amount))}
                                    </span>
                                </label>
                            );
                        })}
                    </div>
                </div>
            )}
        </WizardModalShell>
    );
}
